import { getAuth, signInWithCustomToken } from 'firebase/auth'
import {
  getStorage,
  ref,
  uploadBytes,
  deleteObject,
  getDownloadURL
} from 'firebase/storage'
import { getAnalytics, logEvent as analyticsLogEvent } from 'firebase/analytics'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  getRemoteConfig,
  fetchConfig,
  activate,
  getValue,
  getBoolean,
  getString,
  getNumber
} from 'firebase/remote-config'

const FirebaseProxy = (function () {
  const logEvent = (event, parameters) => {
    const analytics = getAnalytics()
    return analyticsLogEvent(analytics, event, parameters)
  }

  const storage = () => getStorage()
  const auth = () => getAuth()
  const functions = () => getFunctions()
  const remoteConfig = () => getRemoteConfig()

  const createStorageRef = fileLocation => {
    return ref(getStorage(), fileLocation)
  }

  const createStorageRefFromUrl = fileUrl => {
    return ref(getStorage(), fileUrl)
  }

  const putFile = (fileRef, file, metadata) => {
    return uploadBytes(fileRef, file, metadata)
  }

  const deleteFile = fileRef => {
    return deleteObject(fileRef)
  }

  const getDownloadUrl = fileRef => getDownloadURL(fileRef)

  const signInWithCustomAuthToken = authToken => {
    const authInstance = getAuth()
    return signInWithCustomToken(authInstance, authToken)
  }

  const setRemoteConfigDefaults = defaults => {
    const remoteConfigInstance = getRemoteConfig()
    remoteConfigInstance.defaultConfig = defaults
    return Promise.resolve()
  }

  const fetchRemoteConfig = minimumFetchIntervalMillis => {
    const remoteConfigInstance = getRemoteConfig()
    remoteConfigInstance.minimumFetchIntervalMillis = minimumFetchIntervalMillis
    return fetchConfig(remoteConfigInstance)
  }

  const activateRemoteConfig = () => {
    const remoteConfigInstance = getRemoteConfig()
    return activate(remoteConfigInstance)
  }

  const getRemoteConfigValue = key => {
    const remoteConfigInstance = getRemoteConfig()
    return getValue(remoteConfigInstance, key)
  }

  const getRemoteConfigBoolean = key => {
    const remoteConfigInstance = getRemoteConfig()
    return getBoolean(remoteConfigInstance, key)
  }

  const getRemoteConfigNumber = key => {
    const remoteConfigInstance = getRemoteConfig()
    return getNumber(remoteConfigInstance, key)
  }

  const getRemoteConfigString = key => {
    const remoteConfigInstance = getRemoteConfig()
    return getString(remoteConfigInstance, key)
  }

  // Crashlytics is not available on web, so provide stub functions
  const crashlytics = () => null

  const logError = (error, message) => {
    // On web, just log to console in development
    if (__DEV__) {
      console.error('Crashlytics logError:', error, message)
    }
    return Promise.resolve()
  }

  const logMessage = message => {
    // On web, just log to console in development
    if (__DEV__) {
      console.log('Crashlytics log:', message)
    }
    return Promise.resolve()
  }

  const setCrashlyticsUserId = userId => {
    // On web, just log to console in development
    if (__DEV__) {
      console.log('Crashlytics setUserId:', userId)
    }
    return Promise.resolve()
  }

  const setCrashlyticsAttributes = attributes => {
    // On web, just log to console in development
    if (__DEV__) {
      console.log('Crashlytics setAttributes:', attributes)
    }
    return Promise.resolve()
  }

  const createHttpsCallable = functionName => {
    const functionsInstance = getFunctions()
    return httpsCallable(functionsInstance, functionName)
  }

  return {
    logEvent,
    storage,
    auth,
    createStorageRef,
    createStorageRefFromUrl,
    putFile,
    deleteFile,
    getDownloadUrl,
    functions,
    remoteConfig,
    signInWithCustomAuthToken,
    setRemoteConfigDefaults,
    fetchRemoteConfig,
    activateRemoteConfig,
    getRemoteConfigValue,
    getRemoteConfigBoolean,
    getRemoteConfigNumber,
    getRemoteConfigString,
    crashlytics,
    logError,
    logMessage,
    setCrashlyticsUserId,
    setCrashlyticsAttributes,
    createHttpsCallable
  }
})()

export default FirebaseProxy
